import React, { Component } from 'react';
import { translate } from 'react-i18next';
import { Panel, Col, Row, Button, ButtonGroup, FormGroup, FormControl, ControlLabel } from 'react-bootstrap';
import Navigation from '../components/Navigation';
import AuditLogFilters from '../components/AuditLogFilters';
import AuditLogsTable from '../components/AuditLogsTable';
import AuditLogDetailModal from '../components/AuditLogDetailModal';
import PaginationPanel from '../components/PaginationPanel';
import DismissableAlert from '../components/DismissableAlert';
import config from '../config';
import axios from 'axios';
import Moment from 'moment';
import './OrganisationAuditLogs.css';

axios.defaults.withCredentials = true;

class GlobalAuditLogs extends Component {
    constructor(props) {
        super(props);

        // Default to last 7 days
        const defaultStartDate = Moment().subtract(7, 'days').toISOString();
        const defaultEndDate = Moment().toISOString();

        this.state = {
            auditLogs: [],
            loading: false,
            exporting: false,
            nextToken: null,
            hasMore: false,
            excludeRead: true, // Exclude read actions by default
            activeQuickFilter: null, // Track which quick filter is active
            filters: {
                action: '',
                actionCategory: '',
                ipAddress: '',
                startDate: defaultStartDate,
                endDate: defaultEndDate
            },
            selectedLog: null,
            showDetailModal: false,
            error: {
                hidden: true,
                title: '',
                message: '',
                style: 'danger'
            }
        };
    }

    componentDidMount() {
        this.loadAuditLogs();
    }

    loadAuditLogs = (loadMore = false) => {
        const self = this;
        this.setState({ loading: true });

        const params = {
            limit: 50
        };

        // Add filters
        if (this.state.filters.action) params.action = this.state.filters.action;
        if (this.state.filters.actionCategory) params.actionCategory = this.state.filters.actionCategory;
        if (this.state.filters.ipAddress) params.ipAddress = this.state.filters.ipAddress;
        if (this.state.filters.startDate) params.startDate = this.state.filters.startDate;
        if (this.state.filters.endDate) params.endDate = this.state.filters.endDate;

        // Exclude read actions by default (unless user specifically filters for them)
        if (this.state.excludeRead && !this.state.filters.action && !this.state.filters.actionCategory) {
            params.excludeActions = 'read';
        }

        // Add pagination token for load more
        if (loadMore && this.state.nextToken) {
            params.nextToken = this.state.nextToken;
        }

        console.log('Loading global audit logs with params:', params);

        axios
            .get(`${config.baseURL}/audit-logs`, { params })
            .then(function(response) {
                console.log('Global audit logs response:', response.data);
                const newLogs = response.data.auditLogs || [];

                self.setState({
                    auditLogs: loadMore ? [...self.state.auditLogs, ...newLogs] : newLogs,
                    nextToken: response.data.nextToken,
                    hasMore: response.data.hasMore,
                    loading: false,
                    error: { hidden: true, title: '', message: '', style: 'danger' }
                });
            })
            .catch(function(error) {
                console.error('Global audit logs error:', error);
                console.error('Error response:', error.response);
                const errorMessage = error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : error.message || 'Failed to load audit logs';
                const errorDetails = error.response && error.response.data && error.response.data.details
                    ? ` - ${JSON.stringify(error.response.data.details)}`
                    : '';
                self.setState({
                    loading: false,
                    error: {
                        hidden: false,
                        title: 'Error:',
                        message: errorMessage + errorDetails,
                        style: 'danger'
                    }
                });
            });
    };

    handleFilterChange = (newFilters) => {
        this.setState(
            {
                filters: {
                    ...this.state.filters,
                    ...newFilters
                },
                nextToken: null,
                auditLogs: []
            },
            () => {
                this.loadAuditLogs(false);
            }
        );
    };

    handleClearFilters = () => {
        const defaultStartDate = Moment().subtract(7, 'days').toISOString();
        const defaultEndDate = Moment().toISOString();

        this.setState({
            activeQuickFilter: null,
            filters: {
                action: '',
                actionCategory: '',
                ipAddress: '',
                startDate: defaultStartDate,
                endDate: defaultEndDate
            },
            nextToken: null,
            auditLogs: []
        }, () => {
            this.loadAuditLogs(false);
        });
    };

    handleQuickFilter = (filterType) => {
        const { t } = this.props;
        let newFilters = { ...this.state.filters };

        switch (filterType) {
            case 'today':
                newFilters.startDate = Moment().startOf('day').toISOString();
                newFilters.endDate = Moment().endOf('day').toISOString();
                newFilters.action = '';
                break;
            case 'last7days':
                newFilters.startDate = Moment().subtract(7, 'days').toISOString();
                newFilters.endDate = Moment().toISOString();
                newFilters.action = '';
                break;
            case 'last30days':
                newFilters.startDate = Moment().subtract(30, 'days').toISOString();
                newFilters.endDate = Moment().toISOString();
                newFilters.action = '';
                break;
            case 'failedLogins':
                newFilters.action = 'login_failed';
                newFilters.startDate = Moment().subtract(7, 'days').toISOString();
                break;
            case 'deletions':
                newFilters.action = 'delete';
                newFilters.startDate = Moment().subtract(7, 'days').toISOString();
                break;
            case 'creates':
                newFilters.action = 'create';
                newFilters.startDate = Moment().subtract(7, 'days').toISOString();
                break;
            case 'updates':
                newFilters.action = 'update';
                newFilters.startDate = Moment().subtract(7, 'days').toISOString();
                break;
            default:
                break;
        }

        this.setState({ activeQuickFilter: filterType }, () => {
            this.handleFilterChange(newFilters);
        });
    };

    handleLoadMore = () => {
        this.loadAuditLogs(true);
    };

    handleViewDetails = (log) => {
        this.setState({
            selectedLog: log,
            showDetailModal: true
        });
    };

    handleCloseDetailModal = () => {
        this.setState({
            showDetailModal: false,
            selectedLog: null
        });
    };

    handleExportCSV = () => {
        const self = this;
        this.setState({ exporting: true });

        const params = {};

        // Add filters
        if (this.state.filters.action) params.action = this.state.filters.action;
        if (this.state.filters.startDate) params.startDate = this.state.filters.startDate;
        if (this.state.filters.endDate) params.endDate = this.state.filters.endDate;

        axios
            .get(`${config.baseURL}/audit-logs/export/csv`, {
                params,
                responseType: 'blob'
            })
            .then(function(response) {
                // Create blob link to download
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;

                const filename = `global-audit-logs-${Moment().format('YYYY-MM-DD')}.csv`;
                link.setAttribute('download', filename);

                document.body.appendChild(link);
                link.click();
                link.remove();

                self.setState({ exporting: false });
            })
            .catch(function(error) {
                console.log(error);
                self.setState({
                    exporting: false,
                    error: {
                        hidden: false,
                        title: 'Error:',
                        message: 'Failed to export CSV',
                        style: 'danger'
                    }
                });
            });
    };

    render() {
        const { t } = this.props;
        document.title = `MySign Digital Screen Solutions | ${t('AuditLogs.globalTitle') || 'Global Audit Logs'}`;

        const childProps = {
            currentOrganisation: this.props.currentOrganisation,
            user: this.props.user,
            userHasAuthenticated: this.props.userHasAuthenticated,
            userIsAuthorized: this.props.userIsAuthorized,
            changeOrganisation: this.props.changeOrganisation,
            match: this.props.match
        };

        return (
            <div className="OrganisationAuditLogs">
                <div id="wrapper">
                    <Navigation props={this.props} />
                    <div id="content-wrapper">
                        <Row>
                            <Col lg={12}>
                            <h1>{t('AuditLogs.globalTitle') || 'Global Audit Logs'}</h1>

                            <DismissableAlert
                                hidden={this.state.error.hidden}
                                title={this.state.error.title}
                                message={this.state.error.message}
                                style={this.state.error.style}
                            />

                            <div className="quick-filters">
                                <div style={{ marginBottom: '10px' }}>
                                    <p style={{ margin: 0 }}><strong>{t('AuditLogs.quickFilters')}:</strong></p>
                                </div>
                                <ButtonGroup>
                                    <Button
                                        bsSize="small"
                                        bsStyle={this.state.activeQuickFilter === 'creates' ? 'success' : 'default'}
                                        onClick={() => this.handleQuickFilter('creates')}
                                        active={this.state.activeQuickFilter === 'creates'}
                                    >
                                        <i className="fa fa-plus-circle" /> Creates
                                    </Button>
                                    <Button
                                        bsSize="small"
                                        bsStyle={this.state.activeQuickFilter === 'updates' ? 'info' : 'default'}
                                        onClick={() => this.handleQuickFilter('updates')}
                                        active={this.state.activeQuickFilter === 'updates'}
                                    >
                                        <i className="fa fa-edit" /> Updates
                                    </Button>
                                    <Button
                                        bsSize="small"
                                        bsStyle={this.state.activeQuickFilter === 'deletions' ? 'danger' : 'default'}
                                        onClick={() => this.handleQuickFilter('deletions')}
                                        active={this.state.activeQuickFilter === 'deletions'}
                                    >
                                        <i className="fa fa-trash" /> {t('AuditLogs.deletions')}
                                    </Button>
                                    <Button
                                        bsSize="small"
                                        bsStyle={this.state.activeQuickFilter === 'failedLogins' ? 'warning' : 'default'}
                                        onClick={() => this.handleQuickFilter('failedLogins')}
                                        active={this.state.activeQuickFilter === 'failedLogins'}
                                    >
                                        <i className="fa fa-exclamation-triangle" /> {t('AuditLogs.failedLogins')}
                                    </Button>
                                </ButtonGroup>
                                <div style={{ marginTop: '10px' }}>
                                    <ButtonGroup>
                                        <Button
                                            bsSize="small"
                                            active={this.state.activeQuickFilter === 'today'}
                                            onClick={() => this.handleQuickFilter('today')}
                                        >
                                            {t('AuditLogs.today')}
                                        </Button>
                                        <Button
                                            bsSize="small"
                                            active={this.state.activeQuickFilter === 'last7days'}
                                            onClick={() => this.handleQuickFilter('last7days')}
                                        >
                                            {t('AuditLogs.last7Days')}
                                        </Button>
                                        <Button
                                            bsSize="small"
                                            active={this.state.activeQuickFilter === 'last30days'}
                                            onClick={() => this.handleQuickFilter('last30days')}
                                        >
                                            {t('AuditLogs.last30Days')}
                                        </Button>
                                    </ButtonGroup>
                                </div>
                            </div>

                            <AuditLogFilters
                                filters={this.state.filters}
                                onFilterChange={this.handleFilterChange}
                            />

                            <PaginationPanel
                                heading={
                                    <span>
                                        <i className="fa fa-clipboard-list" /> {t('AuditLogs.globalTitle') || 'Global Audit Logs'}
                                        {this.state.loading && (
                                            <span className="loading-indicator"> ({t('AuditLogs.loading')})</span>
                                        )}
                                    </span>
                                }
                                pagination={
                                    this.state.hasMore ? (
                                        <Button
                                            onClick={this.handleLoadMore}
                                            disabled={this.state.loading}
                                            bsStyle="primary"
                                        >
                                            {this.state.loading ? (
                                                <span><i className="fa fa-spinner fa-spin" /> Loading...</span>
                                            ) : (
                                                <span><i className="fa fa-angle-down" /> {t('AuditLogs.loadMore')}</span>
                                            )}
                                        </Button>
                                    ) : null
                                }
                            >
                                <AuditLogsTable
                                    logs={this.state.auditLogs}
                                    onViewDetails={this.handleViewDetails}
                                />
                            </PaginationPanel>
                        </Col>
                    </Row>
                </div>
            </div>

            <AuditLogDetailModal
                show={this.state.showDetailModal}
                onHide={this.handleCloseDetailModal}
                log={this.state.selectedLog}
            />
        </div>
        );
    }
}

export default translate('translations')(GlobalAuditLogs);
